import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomFieldTypeEnum } from '../../enums/custom-field-type.enum';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { CustomFieldsConfig } from '../../classes/custom-fields.class';
import { CustomFieldsService } from '../../services/custom-fields.service';
import { MessageService } from '../../services/message.service';
import { ICustomFieldsValues } from '../../typing/custom-fields-config.interface';
import { AuthService } from '../auth/auth.service';
import { OrderStatusEnum } from '../../enums/status.enum';
import { Subject, takeUntil } from 'rxjs';
import { OfflineService } from '../offline/offline.service';

@Component({
    selector: 'app-order-custom-fields',
    standalone: true,
    imports: [CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule, FormsModule, MatCheckbox, MatFormField, MatInput, MatLabel, MatSlideToggle],
    templateUrl: './order-custom-fields.component.html',
    styleUrl: './order-custom-fields.component.scss'
})
export class OrderCustomFieldsComponent implements OnInit, OnChanges, OnDestroy {
    @Input({required: true}) customFieldsParamsControl!: FormControl;
    @Input({required: true}) projectId!: number;
    @Input() status?: OrderStatusEnum;
    @Output() changeForm=new EventEmitter();
    @Output() customFieldInvalid =new EventEmitter();
    customFields: CustomFieldsConfig[] = [];
    customFieldsForm: FormGroup;
    CustomFieldTypeEnum = CustomFieldTypeEnum;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private customFieldsService: CustomFieldsService,
        private messageService: MessageService,
        private authService: AuthService,
        private offlineService: OfflineService,
    ) {
        this.customFieldsForm = this.fb.group({});
    }

    ngOnChanges():void {
        if (this.authService.isExecutor() && this.offlineService.offline()) {
            this.initForm();
            this.checkFormValidity();
        }
    }

    ngOnInit(): void {
        this.loadCustomFields();
        this.checkFormValidity();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    initForm(): void {
        this.customFields.forEach(field => {
            this.customFieldsForm.addControl(`customField_${field.data.id}`, this.fb.control(''));
        });
        if (this.customFieldsParamsControl.value) {
            this.setCustomFieldValues(this.customFieldsParamsControl.value);
        }

        if (this.status === OrderStatusEnum.atwork || this.status === OrderStatusEnum.done && this.authService.isExecutor()) {
            this.customFieldsForm.enable();
        } else { this.customFieldsForm.disable(); }
    }

    getCustomFieldsValue(): ICustomFieldsValues[] {
        return this.customFields.map(field => ({
            key: field.data.id,
            value: this.customFieldsForm.get(`customField_${field.data.id}`)?.value
        }));
    }

    setCustomFieldValues(values: ICustomFieldsValues[]): void {
        values.forEach(value => {
            const control = this.customFieldsForm.get(`customField_${value.key}`);
            if (control) {
                control.setValue(value.value);
            }
        });
    }

    loadCustomFields(): void {
        const customFields$ = this.authService.isExecutor() && this.offlineService.offline() ?
            this.offlineService.getCustomFieldsConfigsCache().pipe(takeUntil(this.unsubscribe$)):
            this.customFieldsService.getCustomFieldsConfigs({ 'filter.project.id': this.projectId });

        customFields$.subscribe({
            next: (fields) => {
                this.customFields = fields.data;
                this.initForm();
            },
            error: () => this.messageService.errorMessage('Ошибка загрузки настраиваемых полей')
        });
    }

    // Проверяем валидность формы и отправляем эмит
    checkFormValidity():void {
        this.customFieldsForm.valueChanges.subscribe(() => {
            const fields = this.customFields.map(field => ({
                required: field.data.required,
                value: this.customFieldsForm.get(`customField_${field.data.id}`)?.value
            }));
            const isValid = fields.every(field => !field.required || field.value);
            this.customFieldInvalid.emit(!isValid);
        });
    }
}
